import { useEffect, useRef } from 'react';
import {
  CreditsTransactionStatus,
  CreditsTransactionStatusRequest,
  CreditsTransactionType,
  PurchaseCreditsType,
} from '../../components/settings/components/billing-subscription/types/purchase-credits';
import { executeOnRequestStatus } from '../utils';
import { RequestStatus } from '../enums/request-status';

interface UseCreditsTransactionPollingProps {
  /**
   * Whether polling should be active
   */
  isPolling: boolean;
  /**
   * Type of credits being purchased
   */
  type: PurchaseCreditsType;
  /**
   * Order ID from the purchase response
   */
  orderId?: string;
  /**
   * Request status for the transaction status API call
   */
  transactionStatusRequestStatus: RequestStatus;
  /**
   * Response from the transaction status API
   */
  transactionStatusResponse?: {
    status: CreditsTransactionStatus;
    shouldPoll: boolean;
  };
  /**
   * Function to call the transaction status API
   */
  sendGetCreditsTransactionStatusRequest: (
    payload: CreditsTransactionStatusRequest,
  ) => void;
  /**
   * Function to reset the purchase credits request
   */
  resetPurchaseCreditsRequest: () => void;
  /**
   * Function to reset the transaction status request
   */
  resetGetCreditsTransactionStatusRequest: () => void;
  /**
   * Callback when transaction succeeds
   */
  onTransactionSuccess?: () => void;
  /**
   * Callback when transaction fails
   */
  onTransactionFailed?: () => void;
  /**
   * Callback when polling completes (success, failure, or timeout)
   */
  onPollingComplete?: () => void;
  /**
   * Polling interval in milliseconds (default: 3000ms)
   */
  pollingInterval?: number;
  /**
   * Maximum polling duration in milliseconds (default: 120000ms / 2 minutes)
   */
  maxPollingDuration?: number;
}

/**
 * Custom hook to handle polling for credits transaction status
 * Polls the transaction status API at regular intervals until:
 * 1. Transaction status is FAILED
 * 2. shouldPoll flag is false
 * 3. Maximum polling duration is reached
 */
const useCreditsTransactionPolling = ({
  isPolling,
  type,
  orderId,
  transactionStatusRequestStatus,
  transactionStatusResponse,
  sendGetCreditsTransactionStatusRequest,
  resetPurchaseCreditsRequest,
  resetGetCreditsTransactionStatusRequest,
  onTransactionSuccess,
  onTransactionFailed,
  onPollingComplete,
  pollingInterval = 3000,
  maxPollingDuration = 120000,
}: UseCreditsTransactionPollingProps) => {
  // Refs for polling management
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingAttemptsRef = useRef<number>(0);
  const maxPollingAttemptsRef = useRef<number>(
    maxPollingDuration / pollingInterval,
  );
  const hasInitiatedPollingRef = useRef<boolean>(false);

  // Use ref for orderId to always capture the latest value
  const orderIdRef = useRef<string | undefined>(orderId);

  // Use refs for callbacks to prevent unnecessary effect re-runs
  const onTransactionSuccessRef = useRef(onTransactionSuccess);
  const onTransactionFailedRef = useRef(onTransactionFailed);
  const onPollingCompleteRef = useRef(onPollingComplete);

  // Update refs when they change
  useEffect(() => {
    orderIdRef.current = orderId;
    onTransactionSuccessRef.current = onTransactionSuccess;
    onTransactionFailedRef.current = onTransactionFailed;
    onPollingCompleteRef.current = onPollingComplete;
  }, [orderId, onTransactionSuccess, onTransactionFailed, onPollingComplete]);

  // Cleanup function to stop polling
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    hasInitiatedPollingRef.current = false;
    pollingAttemptsRef.current = 0;
    orderIdRef.current = undefined;
  };

  // Effect to handle polling interval
  useEffect(() => {
    if (!isPolling) {
      hasInitiatedPollingRef.current = false;
      return undefined;
    }

    // Prevent duplicate polling if already initiated
    if (hasInitiatedPollingRef.current) {
      return undefined;
    }

    // Update orderId ref synchronously to ensure we use the latest value
    orderIdRef.current = orderId;

    // Validate orderId is present before starting polling
    if (!orderIdRef.current) {
      return undefined;
    }

    const orderIdNum = parseInt(orderIdRef.current, 10);

    // Mark polling as initiated
    hasInitiatedPollingRef.current = true;

    // Reset polling attempts when starting new polling session
    pollingAttemptsRef.current = 0;

    // Make the first polling request immediately
    const initialPayload: CreditsTransactionStatusRequest = {
      type: CreditsTransactionType[type],
      orderId: orderIdNum,
    };
    sendGetCreditsTransactionStatusRequest(initialPayload);

    // Setup interval for subsequent polling requests
    pollingIntervalRef.current = setInterval(() => {
      pollingAttemptsRef.current += 1;

      // Check if max attempts reached
      if (pollingAttemptsRef.current >= maxPollingAttemptsRef.current) {
        // Stop polling after max duration
        stopPolling();
        resetPurchaseCreditsRequest();
        resetGetCreditsTransactionStatusRequest();

        // Assume success after timeout
        onTransactionSuccessRef.current?.();
        onPollingCompleteRef.current?.();
      } else {
        // Continue polling - orderId is guaranteed to be present
        const pollPayload: CreditsTransactionStatusRequest = {
          type: CreditsTransactionType[type],
          orderId: orderIdNum,
        };
        sendGetCreditsTransactionStatusRequest(pollPayload);
      }
    }, pollingInterval);

    // Cleanup on unmount or when polling stops
    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPolling, type, orderId]);

  // Effect to handle polling response
  useEffect(() => {
    if (!isPolling) {
      return;
    }

    executeOnRequestStatus({
      status: transactionStatusRequestStatus,
      onSuccess: () => {
        const status = transactionStatusResponse?.status;
        const shouldPoll = transactionStatusResponse?.shouldPoll;

        // Stop polling when shouldPoll is false or transaction failed
        // Note: Polling continues even when status is PAID until shouldPoll becomes false
        if (
          status === CreditsTransactionStatus.FAILED ||
          shouldPoll === false
        ) {
          // Clear polling interval
          stopPolling();
          resetPurchaseCreditsRequest();
          resetGetCreditsTransactionStatusRequest();

          if (status === CreditsTransactionStatus.FAILED) {
            onTransactionFailedRef.current?.();
            onPollingCompleteRef.current?.();
          } else {
            // shouldPoll is false - transaction successful
            onTransactionSuccessRef.current?.();
            onPollingCompleteRef.current?.();
          }
        }
      },
      onFailed: () => {
        // On API failure, continue polling until max attempts
        stopPolling();
        resetPurchaseCreditsRequest();
        resetGetCreditsTransactionStatusRequest();
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPolling, transactionStatusRequestStatus, transactionStatusResponse]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      stopPolling();
    },
    [],
  );

  return {
    stopPolling,
  };
};

export default useCreditsTransactionPolling;
